import React, { createContext, useContext, useEffect, useState } from 'react'
import api from '../services/axios'

export interface User {
  id: number
  name: string
  nickname: string
  email: string
  role: string
  vinc_marca: string
  status: number
}

interface AuthContextData {
  user: User | null
  loading: boolean
  signed: boolean
  signIn: (userData: User) => void
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me')
      if (response.data && response.data.user) {
        setUser(response.data.user)
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCurrentUser()
  }, [])

  const signIn = (userData: User) => {
    setUser(userData)
  }

  const signOut = async () => {
    try {
      await api.post('/logout')
    } catch (err) {
      console.error('Erro ao fazer logout:', err)
    } finally {
      // Quem já é o cookie derruba é o /logout. Estas remoções ficam para
      // limpar tokens antigos que sobraram no navegador de quem usou as
      // versões que guardavam a sessão no localStorage.
      localStorage.removeItem('authToken')
      localStorage.removeItem('token')
      localStorage.removeItem('@GID:token')
      setUser(null)
      window.location.replace('/')
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signed: !!user,
        signIn,
        signOut,
        refreshUser: fetchCurrentUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth deve ser utilizado dentro de um AuthProvider')
  }
  return context
}
